// import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
// import { useNavigate } from "react-router-dom";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faTimes, faAd, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
// import axiosInstance from '../Datas/AxiosConfig';
// import styles from '../Styles/AdBanner.module.scss';

// interface Advertisement {
//   id: number;
//   title: string;
//   image_url: string | null;
//   content: string | null;
//   url: string | null;
//   display_interval_minutes: number;
//   priority: number;
// }

// interface AdResponse {
//   success: boolean;
//   has_banner: boolean;
//   banner?: Advertisement;
//   message?: string;
// }

// // ============================================
// // Helper Functions (localStorage & consent)
// // ============================================

// const getAdBannerHistory = () => {
//   try {
//     const stored = localStorage.getItem('adBannerHistory');
//     return stored ? JSON.parse(stored) : { lastShown: 0, lastViewTracked: 0 };
//   } catch {
//     return { lastShown: 0, lastViewTracked: 0 };
//   }
// };

// const updateAdBannerHistory = (lastShown: number) => {
//   const history = {
//     lastShown,
//     lastViewTracked: Date.now() // Track when we last sent view tracking
//   };
//   localStorage.setItem('adBannerHistory', JSON.stringify(history));
// };

// const checkMarketingConsent = (): boolean => {
//   const cookiesAccepted = localStorage.getItem('cookiesAccepted');
//   const marketingEnabled = localStorage.getItem('marketingEnabled');
//   return cookiesAccepted === 'all' || 
//          (cookiesAccepted === 'custom' && marketingEnabled === 'true');
// };

// const shouldShowAdBanner = (displayIntervalMinutes: number): boolean => {
//   if (!checkMarketingConsent()) {
//     return false;
//   }
  
//   const history = getAdBannerHistory();
//   const intervalMs = displayIntervalMinutes * 60 * 1000;
//   const timeSinceLastShown = Date.now() - history.lastShown;
  
//   return timeSinceLastShown >= intervalMs;
// };

// // ============================================
// // Main Component
// // ============================================

// const AdBanner: React.FC = React.memo(() => {
//   const navigate = useNavigate();
//   const [isVisible, setIsVisible] = useState<boolean>(false);
//   const [banner, setBanner] = useState<Advertisement | null>(null);

//   // const serverBaseUrl = "https://xeloba.ge/";
  
//   // Refs for stable references
//   const timerRef = useRef<NodeJS.Timeout | null>(null);
//   const statusCheckRef = useRef<NodeJS.Timeout | null>(null);
//   const cookieWatcherRef = useRef<NodeJS.Timeout | null>(null);
//   const componentMountedRef = useRef<boolean>(true);
//   const lastConsentCheckRef = useRef<boolean>(false);
//   const currentBannerIdRef = useRef<number | null>(null);
//   const viewTrackedRef = useRef<boolean>(false);
  
//   // ============================================
//   // API Calls
//   // ============================================
  
//   // Fetch active advertisement
//   const fetchActiveAd = useCallback(async (): Promise<void> => {
//     if (!checkMarketingConsent() || !componentMountedRef.current) {
//       return;
//     }

//     try {
//       const response = await axiosInstance.get<AdResponse>('advertisement/');

//       if (response.data.success && response.data.has_banner && response.data.banner) {
//         const adBanner = response.data.banner;
        
//         // Check if we should show based on interval
//         if (shouldShowAdBanner(adBanner.display_interval_minutes)) {
//           setBanner(adBanner);
//           setIsVisible(true);
//           currentBannerIdRef.current = adBanner.id;
//           viewTrackedRef.current = false; // Reset view tracking
          
//           updateAdBannerHistory(Date.now());
          
//           // Track view after banner is shown
//           trackBannerView(adBanner.id);
//         }
//       }
//     } catch (err: any) {
//       console.error('Error fetching advertisement:', err);
//     }
//   }, []);

//   // Track banner view
//   const trackBannerView = useCallback(async (bannerId: number): Promise<void> => {
//     if (viewTrackedRef.current) return; // Already tracked
    
//     try {
//       await axiosInstance.post(`advertisement/${bannerId}/view/`);
//       viewTrackedRef.current = true;
//       // console.log('Ad view tracked:', bannerId);
//     } catch (err) {
//       console.error('Error tracking ad view:', err);
//     }
//   }, []);

//   // Track banner click
//   const trackBannerClick = useCallback(async (bannerId: number): Promise<string | null> => {
//     try {
//       const response = await axiosInstance.post(`advertisement/${bannerId}/click/`);
//       // console.log('Ad click tracked:', bannerId);
//       return response.data.redirect_url || null;
//     } catch (err) {
//       console.error('Error tracking ad click:', err);
//       return null;
//     }
//   }, []);

//   // Check banner status (every 5 minutes)
//   const checkBannerStatus = useCallback(async (bannerId: number): Promise<void> => {
//     try {
//       const response = await axiosInstance.get(`advertisement/${bannerId}/status/`);
      
//       if (!response.data.is_active) {
//         // console.log('Banner deactivated, hiding...');
//         handleClose();
        
//         // Stop status checking
//         if (statusCheckRef.current) {
//           clearInterval(statusCheckRef.current);
//           statusCheckRef.current = null;
//         }
//       }
//     } catch (err) {
//       console.error('Error checking ad status:', err);
//     }
//   }, []);

//   // ============================================
//   // Banner Display Timer
//   // ============================================
  
//   const startAdTimer = useCallback(() => {
//     if (timerRef.current) {
//       clearInterval(timerRef.current);
//     }

//     // Check every minute if we should fetch new ad
//     timerRef.current = setInterval(() => {
//       if (!componentMountedRef.current) return;
//       if (!checkMarketingConsent()) return;

//       // If currently showing banner, don't fetch
//       if (isVisible && banner) return;

//       fetchActiveAd();
//     }, 60000); // Every minute
//   }, [isVisible, banner, fetchActiveAd]);

//   // ============================================
//   // Status Check Timer (every 5 minutes)
//   // ============================================
  
//   const startStatusCheckTimer = useCallback((bannerId: number) => {
//     if (statusCheckRef.current) {
//       clearInterval(statusCheckRef.current);
//     }

//     // Check status every 5 minutes
//     statusCheckRef.current = setInterval(() => {
//       if (!componentMountedRef.current) return;
      
//       checkBannerStatus(bannerId);
//     }, 5 * 60 * 1000); // 5 minutes
//   }, [checkBannerStatus]);

//   // ============================================
//   // Cookie Consent Watcher
//   // ============================================
  
//   const startCookieWatcher = useCallback(() => {
//     if (cookieWatcherRef.current) {
//       clearInterval(cookieWatcherRef.current);
//     }

//     lastConsentCheckRef.current = checkMarketingConsent();

//     cookieWatcherRef.current = setInterval(() => {
//       if (!componentMountedRef.current) return;
      
//       const currentConsent = checkMarketingConsent();
      
//       if (currentConsent !== lastConsentCheckRef.current) {
//         lastConsentCheckRef.current = currentConsent;
        
//         if (currentConsent) {
//           setTimeout(() => {
//             fetchActiveAd();
//           }, 500);
          
//           // Stop watching once consent is given
//           if (cookieWatcherRef.current) {
//             clearInterval(cookieWatcherRef.current);
//             cookieWatcherRef.current = null;
//           }
//         }
//       }
//     }, 5000); // 5 seconds
//   }, [fetchActiveAd]);

//   // ============================================
//   // Component Lifecycle
//   // ============================================
  
//   useEffect(() => {
//     componentMountedRef.current = true;

//     // Initial fetch
//     if (checkMarketingConsent()) {
//       fetchActiveAd();
//     } else {
//       startCookieWatcher();
//     }

//     // Start main timer
//     startAdTimer();

//     return () => {
//       componentMountedRef.current = false;
      
//       // Cleanup timers
//       if (timerRef.current) {
//         clearInterval(timerRef.current);
//         timerRef.current = null;
//       }
      
//       if (statusCheckRef.current) {
//         clearInterval(statusCheckRef.current);
//         statusCheckRef.current = null;
//       }
      
//       if (cookieWatcherRef.current) {
//         clearInterval(cookieWatcherRef.current);
//         cookieWatcherRef.current = null;
//       }
//     };
//   }, [fetchActiveAd, startAdTimer, startCookieWatcher]);

//   // Start status check when banner is shown
//   useEffect(() => {
//     if (isVisible && banner) {
//       startStatusCheckTimer(banner.id);
//     }
    
//     return () => {
//       if (statusCheckRef.current) {
//         clearInterval(statusCheckRef.current);
//         statusCheckRef.current = null;
//       }
//     };
//   }, [isVisible, banner, startStatusCheckTimer]);

//   // ============================================
//   // Banner Actions
//   // ============================================
  
//   const handleClose = useCallback(() => {
//     setIsVisible(false);
//     setBanner(null);
//     currentBannerIdRef.current = null;
//     viewTrackedRef.current = false;
//   }, []);

//  const handleBannerClick = useCallback(async () => {
//   if (!banner) return;

//   // 1️⃣ Track click
//   const redirectUrl = await trackBannerClick(banner.id);
  
//   // 2️⃣ Close banner
//   handleClose();
  
//   // 3️⃣ Redirect logic
//   if (redirectUrl) {
//     // ✅ Check if URL contains "xeloba.ge" or starts with "/"
//     const isInternalLink = 
//       redirectUrl.includes('xeloba.ge') || 
//       redirectUrl.includes('xeloba') ||
//       redirectUrl.startsWith('/');
    
//     if (isInternalLink) {
//       // ✅ Internal navigation - same tab
//       if (redirectUrl.startsWith('http')) {
//         // Full URL with https://xeloba.ge/...
//         const path = new URL(redirectUrl).pathname;
//         navigate(path);
//       } else {
//         // Relative path like "/vip/packages"
//         navigate(redirectUrl);
//       }
//     } else {
//       // ✅ External link - new tab
//       window.open(redirectUrl, '_blank', 'noopener,noreferrer');
//     }
//   }
// }, [banner, handleClose, navigate, trackBannerClick]);

//   // ============================================
//   // Render
//   // ============================================
  
//   // Early return - no unnecessary JSX creation
//   if (!isVisible || !banner) {
//     return null;
//   }

//   return (
//     <div className={styles.bannerOverlay}>
//       <div className={styles.bannerContainer}>
//         <div className={styles.banner}>
//           {/* Close Button */}
//           <button 
//             className={styles.closeButton}
//             onClick={handleClose}
//             aria-label="რეკლამის დახურვა"
//           >
//             <FontAwesomeIcon icon={faTimes} />
//           </button>

//           {/* Ad Badge */}
//           <div className={styles.adBadge}>
//             <FontAwesomeIcon icon={faAd} className={styles.adIcon} />
//             <span>სარეკლამო</span>
//           </div>

//           {/* Header with Title */}
//           <div className={styles.header}>
//             <h3 className={styles.title}>{banner.title}</h3>
//           </div>

//           {/* Content */}
//           <div className={styles.content}>
//             {/* Image */}
//             {banner.image_url && (
//               <div className={styles.imageContainer}>
//                 <img 
//                   src={banner.image_url}
//                   alt={banner.title}
//                   className={styles.adImage}
//                   loading="lazy"
//                 />
//               </div>
//             )}

//             {/* Description */}
//             {banner.content && (
//               <div className={styles.description}>
//                 <p>{banner.content}</p>
//               </div>
//             )}
//           </div>

//           {/* Actions */}
//           {banner.url && (
//             <div className={styles.actions}>
//               <button 
//                 className={styles.actionButton}
//                 onClick={handleBannerClick}
//               >
//                 <span>დეტალების ნახვა</span>
//                 <FontAwesomeIcon icon={faExternalLinkAlt} className={styles.buttonIcon} />
//               </button>
//             </div>
//           )}

//         </div>
//       </div>
//     </div>
//   );
// });

// AdBanner.displayName = 'AdBanner';

// export default AdBanner;







/////////////////optimized version
// import React, { useState, useEffect, useRef, useCallback } from 'react';
// import { useNavigate } from "react-router-dom";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faTimes, faAd, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
// import axiosInstance from '../Datas/AxiosConfig';
// import styles from '../Styles/AdBanner.module.scss';

// interface Advertisement {
//   id: number;
//   title: string;
//   image_url: string | null;
//   content: string | null;
//   url: string | null;
//   display_interval_minutes: number;
//   priority: number;
// }

// interface AdResponse {
//   success: boolean;
//   has_banner: boolean;
//   banner?: Advertisement;
//   message?: string;
//   from_cache?: boolean;
// }

// // ============================================
// // Helper Functions
// // ============================================

// const getAdBannerHistory = () => {
//   try {
//     const stored = localStorage.getItem('adBannerHistory');
//     return stored ? JSON.parse(stored) : { lastShown: 0, lastClosed: 0 };
//   } catch {
//     return { lastShown: 0, lastClosed: 0 };
//   }
// };

// const updateAdBannerHistory = (lastShown: number, lastClosed?: number) => {
//   const history = getAdBannerHistory();
//   localStorage.setItem('adBannerHistory', JSON.stringify({
//     lastShown,
//     lastClosed: lastClosed !== undefined ? lastClosed : history.lastClosed
//   }));
// };

// const markAdClosed = () => {
//   const history = getAdBannerHistory();
//   localStorage.setItem('adBannerHistory', JSON.stringify({
//     ...history,
//     lastClosed: Date.now()
//   }));
// };

// const checkMarketingConsent = (): boolean => {
//   const cookiesAccepted = localStorage.getItem('cookiesAccepted');
//   const marketingEnabled = localStorage.getItem('marketingEnabled');
//   return cookiesAccepted === 'all' || 
//          (cookiesAccepted === 'custom' && marketingEnabled === 'true');
// };

// const shouldShowAdBanner = (displayIntervalMinutes: number): boolean => {
//   if (!checkMarketingConsent()) {
//     return false;
//   }
  
//   const history = getAdBannerHistory();
//   const intervalMs = displayIntervalMinutes * 60 * 1000;
  
//   const now = Date.now();
//   const lastInteraction = Math.max(history.lastShown, history.lastClosed);
//   const timeSinceLastInteraction = now - lastInteraction;
  
//   return timeSinceLastInteraction >= intervalMs;
// };

// // ============================================
// // Main Component
// // ============================================

// const AdBanner: React.FC = React.memo(() => {
//   const navigate = useNavigate();
//   const [isVisible, setIsVisible] = useState<boolean>(false);
//   const [banner, setBanner] = useState<Advertisement | null>(null);
  
//   const componentMountedRef = useRef<boolean>(true);
//   const viewTrackedRef = useRef<boolean>(false);
//   const fetchTimerRef = useRef<NodeJS.Timeout | null>(null);
//   const isVisibleRef = useRef<boolean>(false);

//   // Sync isVisible state with ref
//   useEffect(() => {
//     isVisibleRef.current = isVisible;
//   }, [isVisible]);

//   // ============================================
//   // API Calls
//   // ============================================
  
//   const trackBannerView = useCallback(async (bannerId: number): Promise<void> => {
//     if (viewTrackedRef.current) return;
    
//     try {
//       await axiosInstance.post(`advertisement/${bannerId}/view/`);
//       viewTrackedRef.current = true;
      
//       if (process.env.NODE_ENV === 'development') {
//         console.log('✅ Ad view tracked:', bannerId);
//       }
//     } catch (err) {
//       console.error('❌ Error tracking ad view:', err);
//     }
//   }, []);

//   const trackBannerClick = useCallback(async (bannerId: number): Promise<string | null> => {
//     try {
//       const response = await axiosInstance.post(`advertisement/${bannerId}/click/`);
      
//       if (process.env.NODE_ENV === 'development') {
//         console.log('✅ Ad click tracked:', bannerId);
//       }
      
//       return response.data.redirect_url || null;
//     } catch (err) {
//       console.error('❌ Error tracking ad click:', err);
//       return null;
//     }
//   }, []);

//   // ✅ fetchActiveAd - Ref-ით სტაბილური ვერსია
//   const fetchActiveAd = useCallback(async (): Promise<void> => {
//     if (!checkMarketingConsent() || !componentMountedRef.current) {
//       return;
//     }

//     try {
//       const response = await axiosInstance.get<AdResponse>('advertisement/');

//       if (response.data.success && response.data.has_banner && response.data.banner) {
//         const adBanner = response.data.banner;
        
//         if (process.env.NODE_ENV === 'development') {
//           console.log('📢 Ad Banner fetched:', {
//             title: adBanner.title,
//             fromCache: response.data.from_cache,
//             interval: adBanner.display_interval_minutes
//           });
//         }
        
//         if (shouldShowAdBanner(adBanner.display_interval_minutes)) {
//           setBanner(adBanner);
//           setIsVisible(true);
//           viewTrackedRef.current = false;
          
//           updateAdBannerHistory(Date.now());
//           trackBannerView(adBanner.id);
//         }
//       }
//     } catch (err: any) {
//       console.error('Error fetching advertisement:', err);
//     }
//   }, [trackBannerView]); // ← მხოლოდ trackBannerView

//   // ============================================
//   // Consent Change Detection
//   // ============================================
  
//   useEffect(() => {
//     // Initial check
//     if (checkMarketingConsent()) {
//       console.log('✅ Initial consent check: has consent, fetching...');
//       fetchActiveAd();
//     } else {
//       console.log('⏳ Initial consent check: waiting for consent...');
//     }
    
//     // ✅ Listen for consent changes
//     const handleConsentChange = () => {
//       console.log('🔔 Consent event received!');
      
//       // Small delay to ensure localStorage is updated
//       setTimeout(() => {
//         if (checkMarketingConsent() && !isVisibleRef.current) {
//           console.log('✅ Consent granted, fetching ad...');
//           fetchActiveAd();
//         }
//       }, 100);
//     };
    
//     window.addEventListener('consentUpdated', handleConsentChange);
    
//     return () => {
//       window.removeEventListener('consentUpdated', handleConsentChange);
//     };
//   }, [fetchActiveAd]); // ← fetchActiveAd არის, მაგრამ stable

//   // ============================================
//   // Timer
//   // ============================================
  
//   useEffect(() => {
//     componentMountedRef.current = true;

//     // Timer - 5 minutes
//     fetchTimerRef.current = setInterval(() => {
//       if (!componentMountedRef.current || !checkMarketingConsent()) return;
      
//       if (!isVisibleRef.current) {
//         fetchActiveAd();
//       }
//     }, 5 * 60 * 1000);

//     return () => {
//       componentMountedRef.current = false;
      
//       if (fetchTimerRef.current) {
//         clearInterval(fetchTimerRef.current);
//         fetchTimerRef.current = null;
//       }
//     };
//   }, [fetchActiveAd]);

//   // ============================================
//   // Banner Actions
//   // ============================================
  
//   const handleClose = useCallback(() => {
//     setIsVisible(false);
//     setBanner(null);
//     viewTrackedRef.current = false;
//     markAdClosed();
//   }, []);

//   const handleBannerClick = useCallback(async () => {
//     if (!banner) return;

//     const redirectUrl = await trackBannerClick(banner.id);
//     handleClose();
    
//     if (redirectUrl) {
//       const isInternalLink = 
//         redirectUrl.includes('xeloba.ge') || 
//         redirectUrl.includes('xeloba') ||
//         redirectUrl.startsWith('/');
      
//       if (isInternalLink) {
//         if (redirectUrl.startsWith('http')) {
//           const path = new URL(redirectUrl).pathname;
//           navigate(path);
//         } else {
//           navigate(redirectUrl);
//         }
//       } else {
//         window.open(redirectUrl, '_blank', 'noopener,noreferrer');
//       }
//     }
//   }, [banner, handleClose, navigate, trackBannerClick]);

//   // ============================================
//   // Render
//   // ============================================
  
//   if (!isVisible || !banner) {
//     return null;
//   }

//   return (
//     <div className={styles.bannerOverlay}>
//       <div className={styles.bannerContainer}>
//         <div className={styles.banner}>
//           <button 
//             className={styles.closeButton}
//             onClick={handleClose}
//             aria-label="რეკლამის დახურვა"
//           >
//             <FontAwesomeIcon icon={faTimes} />
//           </button>

//           <div className={styles.adBadge}>
//             <FontAwesomeIcon icon={faAd} className={styles.adIcon} />
//             <span>სარეკლამო</span>
//           </div>

//           <div className={styles.header}>
//             <h3 className={styles.title}>{banner.title}</h3>
//           </div>

//           <div className={styles.content}>
//             {banner.image_url && (
//               <div className={styles.imageContainer}>
//                 <img 
//                   src={banner.image_url}
//                   alt={banner.title}
//                   className={styles.adImage}
//                   loading="lazy"
//                 />
//               </div>
//             )}

//             {banner.content && (
//               <div className={styles.description}>
//                 <p>{banner.content}</p>
//               </div>
//             )}
//           </div>

//           {banner.url && (
//             <div className={styles.actions}>
//               <button 
//                 className={styles.actionButton}
//                 onClick={handleBannerClick}
//               >
//                 <span>დეტალების ნახვა</span>
//                 <FontAwesomeIcon icon={faExternalLinkAlt} className={styles.buttonIcon} />
//               </button>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// });

// AdBanner.displayName = 'AdBanner';

// export default AdBanner;









/////////// console.log
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faAd, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import axiosInstance from '../Datas/AxiosConfig';
import styles from '../Styles/AdBanner.module.scss';

interface Advertisement {
  id: number;
  title: string;
  image_url: string | null;
  content: string | null;
  url: string | null;
  display_interval_minutes: number;
  priority: number;
}

interface AdResponse {
  success: boolean;
  has_banners: boolean;
  banners?: Advertisement[];
  message?: string;
  from_cache?: boolean;
}

// ============================================
// Helper Functions
// ============================================

const getAdBannerHistory = () => {
  try {
    const stored = localStorage.getItem('adBannerHistory');
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const selectBannerToShow = (banners: Advertisement[]): Advertisement | null => {
  if (!banners || banners.length === 0) return null;
  
  const history = getAdBannerHistory();
  const now = Date.now();
  
  // პრიორიტეტით დალაგება
  const sortedBanners = [...banners].sort((a, b) => b.priority - a.priority);
  
  for (const banner of sortedBanners) {
    const bannerKey = `banner_${banner.id}`;
    const lastShown = history[bannerKey] || 0;
    const intervalMs = banner.display_interval_minutes * 60 * 1000;
    const timeSince = now - lastShown;
    
    if (timeSince >= intervalMs) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ Banner "${banner.title}" ready (${Math.floor(timeSince/60000)}min passed)`);
      }
      return banner;
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.log(`⏰ Banner "${banner.title}" waiting (${Math.floor((intervalMs-timeSince)/60000)}min remaining)`);
      }
    }
  }
  
  return null;
};

const updateBannerHistory = (bannerId: number) => {
  try {
    const history = getAdBannerHistory();
    history[`banner_${bannerId}`] = Date.now();
    localStorage.setItem('adBannerHistory', JSON.stringify(history));
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`💾 Updated history for banner ${bannerId}`);
    }
  } catch (err) {
    console.error('Error updating banner history:', err);
  }
};

const markBannerClosed = (bannerId: number) => {
  try {
    const history = getAdBannerHistory();
    history[`banner_${bannerId}_closed`] = Date.now();
    localStorage.setItem('adBannerHistory', JSON.stringify(history));
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`🚪 Marked banner ${bannerId} as closed`);
    }
  } catch (err) {
    console.error('Error marking banner closed:', err);
  }
};

const checkMarketingConsent = (): boolean => {
  const cookiesAccepted = localStorage.getItem('cookiesAccepted');
  const marketingEnabled = localStorage.getItem('marketingEnabled');
  return cookiesAccepted === 'all' || 
         (cookiesAccepted === 'custom' && marketingEnabled === 'true');
};

// ============================================
// Main Component
// ============================================

const AdBanner: React.FC = React.memo(() => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [banner, setBanner] = useState<Advertisement | null>(null);
  
  const componentMountedRef = useRef<boolean>(true);
  const viewTrackedRef = useRef<boolean>(false);
  const fetchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isVisibleRef = useRef<boolean>(false);

  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);

  // ============================================
  // API Calls
  // ============================================
  
  const trackBannerView = useCallback(async (bannerId: number): Promise<void> => {
    if (viewTrackedRef.current) return;
    
    try {
      await axiosInstance.post(`advertisement/${bannerId}/view/`);
      viewTrackedRef.current = true;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Ad view tracked:', bannerId);
      }
    } catch (err) {
      console.error('❌ Error tracking ad view:', err);
    }
  }, []);

  const trackBannerClick = useCallback(async (bannerId: number): Promise<string | null> => {
    try {
      const response = await axiosInstance.post(`advertisement/${bannerId}/click/`);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Ad click tracked:', bannerId);
      }
      
      return response.data.redirect_url || null;
    } catch (err) {
      console.error('❌ Error tracking ad click:', err);
      return null;
    }
  }, []);

  const fetchActiveAd = useCallback(async (): Promise<void> => {
    if (!checkMarketingConsent() || !componentMountedRef.current) {
      return;
    }

    try {
      const response = await axiosInstance.get<AdResponse>('advertisement/');

      if (process.env.NODE_ENV === 'development') {
        console.log('📥 Backend response:', response.data);
      }

      if (response.data.success && response.data.has_banners && response.data.banners) {
        const banners = response.data.banners;
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`📢 Received ${banners.length} active banners:`);
          banners.forEach(b => 
            console.log(`  - ${b.title}: interval=${b.display_interval_minutes}min, priority=${b.priority}`)
          );
        }
        
        const selectedBanner = selectBannerToShow(banners);
        
        if (selectedBanner) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`✅ Showing banner: ${selectedBanner.title}`);
          }
          
          setBanner(selectedBanner);
          setIsVisible(true);
          viewTrackedRef.current = false;
          
          updateBannerHistory(selectedBanner.id);
          trackBannerView(selectedBanner.id);
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.log('⏰ No banner ready to show yet');
          }
        }
      }
    } catch (err: any) {
      console.error('Error fetching advertisements:', err);
    }
  }, [trackBannerView]);

  // ============================================
  // Consent Change Detection
  // ============================================
  
  useEffect(() => {
    if (checkMarketingConsent()) {
      fetchActiveAd();
    }
    
    const handleConsentChange = () => {
      setTimeout(() => {
        if (checkMarketingConsent() && !isVisibleRef.current) {
          fetchActiveAd();
        }
      }, 100);
    };
    
    window.addEventListener('consentUpdated', handleConsentChange);
    
    return () => {
      window.removeEventListener('consentUpdated', handleConsentChange);
    };
  }, [fetchActiveAd]);

  // ============================================
  // Timer
  // ============================================
  
  useEffect(() => {
    componentMountedRef.current = true;

    fetchTimerRef.current = setInterval(() => {
      if (!componentMountedRef.current || !checkMarketingConsent()) return;
      
      if (!isVisibleRef.current) {
        fetchActiveAd();
      }
    }, 5 * 60 * 1000);

    return () => {
      componentMountedRef.current = false;
      
      if (fetchTimerRef.current) {
        clearInterval(fetchTimerRef.current);
        fetchTimerRef.current = null;
      }
    };
  }, [fetchActiveAd]);

  // ============================================
  // Banner Actions
  // ============================================
  
  const handleClose = useCallback(() => {
    if (banner) {
      markBannerClosed(banner.id);
    }
    
    setIsVisible(false);
    setBanner(null);
    viewTrackedRef.current = false;
  }, [banner]);

  const handleBannerClick = useCallback(async () => {
    if (!banner) return;

    const redirectUrl = await trackBannerClick(banner.id);
    handleClose();
    
    if (redirectUrl) {
      const isInternalLink = 
        redirectUrl.includes('xeloba.ge') || 
        redirectUrl.includes('xeloba') ||
        redirectUrl.startsWith('/');
      
      if (isInternalLink) {
        if (redirectUrl.startsWith('http')) {
          const path = new URL(redirectUrl).pathname;
          navigate(path);
        } else {
          navigate(redirectUrl);
        }
      } else {
        window.open(redirectUrl, '_blank', 'noopener,noreferrer');
      }
    }
  }, [banner, handleClose, navigate, trackBannerClick]);

  // ============================================
  // Render
  // ============================================
  
  if (!isVisible || !banner) {
    return null;
  }

  return (
    <div className={styles.bannerOverlay}>
      <div className={styles.bannerContainer}>
        <div className={styles.banner}>
          <button 
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="რეკლამის დახურვა"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>

          <div className={styles.adBadge}>
            <FontAwesomeIcon icon={faAd} className={styles.adIcon} />
            <span>სარეკლამო</span>
          </div>

          <div className={styles.header}>
            <h3 className={styles.title}>{banner.title}</h3>
          </div>

          <div className={styles.content}>
            {banner.image_url && (
              <div className={styles.imageContainer}>
                <img 
                  src={banner.image_url}
                  alt={banner.title}
                  className={styles.adImage}
                  loading="lazy"
                />
              </div>
            )}

            {banner.content && (
              <div className={styles.description}>
                <p>{banner.content}</p>
              </div>
            )}
          </div>

          {banner.url && (
            <div className={styles.actions}>
              <button 
                className={styles.actionButton}
                onClick={handleBannerClick}
              >
                <span>დეტალების ნახვა</span>
                <FontAwesomeIcon icon={faExternalLinkAlt} className={styles.buttonIcon} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

AdBanner.displayName = 'AdBanner';

export default AdBanner;